/* Websites: list a customer's sites and order a new one.
 *
 * Customers never see Hestia. Ordering records the site in its first state;
 * until provisioning is wired the state reads "Queued", never "Live", so the
 * page cannot claim a site exists that does not.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../api";

interface HostingPackage {
  code: string;
  name: string;
  web_domains_limit: number;
  disk_gb_included: number;
  databases: number;
  mailboxes_included: number;
}

interface Website {
  id: string;
  fqdn: string;
  state: string;
  package_code: string;
  subscription_state: string;
  mail_enabled: boolean;
  disk_used_bytes: number | null;
  created_at: string;
}

const STATE_LABEL: Record<string, string> = {
  pending: "Queued",
  domain_added: "Setting up",
  route_ready: "Setting up",
  cert_ready: "Setting up",
  dns_published: "Setting up",
  live: "Live",
  suspended: "Suspended",
  deleting: "Removing",
  failed: "Failed",
};

export function Websites() {
  const queryClient = useQueryClient();
  const [fqdn, setFqdn] = useState("");
  const [packageCode, setPackageCode] = useState("starter");
  const [email, setEmail] = useState(false);

  const packages = useQuery({
    queryKey: ["websites", "packages"],
    queryFn: () => api.get<HostingPackage[]>("/api/websites/packages"),
  });
  const sites = useQuery({
    queryKey: ["websites"],
    queryFn: () => api.get<Website[]>("/api/websites"),
  });

  const order = useMutation({
    mutationFn: () =>
      api.post<Website>("/api/websites", { package_code: packageCode, fqdn, email }),
    onSuccess: () => {
      setFqdn("");
      void queryClient.invalidateQueries({ queryKey: ["websites"] });
    },
  });

  return (
    <section>
      <div className="page-head">
        <div>
          <p className="label">Hosting</p>
          <h1>Websites</h1>
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (fqdn.trim()) order.mutate();
        }}
      >
        <input
          value={fqdn}
          onChange={(event) => setFqdn(event.target.value)}
          placeholder="example.co.za"
          aria-label="Domain name"
        />
        <select
          value={packageCode}
          onChange={(event) => setPackageCode(event.target.value)}
          aria-label="Package"
        >
          {(packages.data ?? []).map((p) => (
            <option key={p.code} value={p.code}>
              {p.name} — {p.disk_gb_included} GB, {p.web_domains_limit}{" "}
              {p.web_domains_limit === 1 ? "site" : "sites"}
            </option>
          ))}
        </select>
        <label>
          <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />{" "}
          Add email (extra cost)
        </label>
        <button type="submit" disabled={order.isPending || !fqdn.trim()}>
          Add website
        </button>
      </form>
      {order.isError && <p className="bad">{(order.error as Error).message}</p>}
      {order.isSuccess && (
        <p className="muted">
          Website recorded. Automatic setup is not switched on yet, so it stays queued.
        </p>
      )}

      {sites.isLoading && <p className="muted">Loading…</p>}
      {sites.isError && <p className="bad">{(sites.error as Error).message}</p>}

      <table>
        <thead>
          <tr>
            <th>Domain</th>
            <th>Package</th>
            <th>Status</th>
            <th>Email</th>
          </tr>
        </thead>
        <tbody>
          {(sites.data ?? []).map((site) => (
            <tr key={site.id}>
              <td>{site.fqdn}</td>
              <td>{site.package_code}</td>
              <td>{STATE_LABEL[site.state] ?? site.state}</td>
              <td>{site.mail_enabled ? "Requested" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {sites.data?.length === 0 && <p className="muted">No websites yet.</p>}
    </section>
  );
}
